import { useState } from 'react';
import { Card } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useOpLogs, useDevices, useDeviceHealth } from '../hooks/useBiometricAdmin';
import { istDateTime, relativeTime } from '../biometricTypes';

/** Negative op codes are Zynx-side protocol traces, not device operations. */
const TRACE_CODES: Record<number, string> = {
  '-1': 'Device error',
  '-2': 'Server error',
  '-3': 'Device discovery',
  '-4': 'Auth rejected',
  '-5': 'Handshake',
  '-6': 'Unhandled table',
  '-7': 'Bad ATTLOG rows',
  '-8': 'Unknown OPERLOG row',
  '-9': 'Unhandled route',
  '-100': 'Admin action',
};

export function DeviceOpLogPanel() {
  const { data: devices } = useDevices(0);
  const { data: health } = useDeviceHealth();
  const [sn, setSn] = useState('');
  const { data: logs, isLoading } = useOpLogs(sn || undefined);

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h2 className="mb-3 text-lg font-semibold text-on-surface">Device health</h2>
        {!health?.length ? (
          <p className="text-sm text-on-surface-variant">No devices registered.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {health.map(h => (
              <div key={h.serial_number} className="rounded-md border border-outline-variant p-3 text-sm">
                <div className="mb-1 flex items-center justify-between">
                  <span className="font-semibold text-on-surface">{h.name || h.serial_number}</span>
                  <Badge tone={h.online ? 'success' : 'danger'}>{h.online ? 'online' : 'offline'}</Badge>
                </div>
                <dl className="space-y-0.5 text-xs text-on-surface-variant">
                  <div>Heartbeat: {relativeTime(h.last_heartbeat_at)}</div>
                  <div>Last punch: {relativeTime(h.last_punch_at)}</div>
                  <div>
                    Queue: {h.pending_commands} pending
                    {h.failed_commands > 0 && (
                      <span className="text-danger"> · {h.failed_commands} failed</span>
                    )}
                  </div>
                </dl>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-on-surface">Device log</h2>
            <p className="text-sm text-on-surface-variant">
              What the machines report and what admins do — enrolments, door events, handshakes and errors.
            </p>
          </div>
          <select
            className="h-9 rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
            value={sn}
            onChange={e => setSn(e.target.value)}
          >
            <option value="">All devices</option>
            {(devices ?? []).map(d => (
              <option key={d.serial_number} value={d.serial_number}>
                {d.name || d.serial_number}
              </option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className="flex justify-center p-8"><Spinner /></div>
        ) : !logs?.length ? (
          <p className="py-8 text-center text-sm text-on-surface-variant">Nothing logged yet.</p>
        ) : (
          <div className="max-h-[560px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface">
                <tr className="border-b border-outline-variant text-left text-xs uppercase tracking-wide text-on-surface-variant">
                  <th className="px-2 py-2">When</th>
                  <th className="px-2 py-2">Device</th>
                  <th className="px-2 py-2">PIN</th>
                  <th className="px-2 py-2">Event</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log._id} className="border-b border-outline-variant/50">
                    <td className="whitespace-nowrap px-2 py-1.5 font-mono text-xs text-on-surface-variant">
                      {istDateTime(log.op_time || log.created_at)}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">{log.device_sn || '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">{log.target_pin || '—'}</td>
                    <td className="px-2 py-1.5">
                      {log.op_code < 0 && (
                        <Badge tone={log.op_code === -100 ? 'primary' : 'neutral'} className="mr-1">
                          {TRACE_CODES[log.op_code] ?? 'trace'}
                        </Badge>
                      )}
                      <span className="text-on-surface">{log.description}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
